import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Share2, 
  Loader2, 
  Play, 
  Linkedin, 
  Instagram, 
  Twitter, 
  Youtube, 
  Facebook,
  TrendingUp,
  Users,
  BarChart3,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformIcons = {
  linkedin: { icon: Linkedin, color: "text-blue-600", bg: "bg-blue-100" },
  instagram: { icon: Instagram, color: "text-pink-600", bg: "bg-pink-100" },
  twitter: { icon: Twitter, color: "text-sky-500", bg: "bg-sky-100" },
  youtube: { icon: Youtube, color: "text-red-600", bg: "bg-red-100" },
  facebook: { icon: Facebook, color: "text-blue-700", bg: "bg-blue-100" },
  tiktok: { icon: TrendingUp, color: "text-gray-800", bg: "bg-gray-100" },
};

export default function SocialMedia() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [stats, setStats] = useState(null);
  const [latestRun, setLatestRun] = useState(null);
  const [posts, setPosts] = useState([]);
  const [insights, setInsights] = useState(null);
  const [expandedInsights, setExpandedInsights] = useState(new Set(['platform_insights']));

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, latestRes] = await Promise.all([
        axios.get(`${API}/social/stats?region=${region}&brand=${brand}`),
        axios.get(`${API}/social/latest?region=${region}&brand=${brand}`)
      ]);
      setStats(statsRes.data);
      setLatestRun(latestRes.data.run);
      setPosts(latestRes.data.posts || []);
      setInsights(latestRes.data.insights);
    } catch (error) {
      console.error("Failed to load social data:", error);
    } finally {
      setLoading(false);
    }
  }, [region, brand]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData, brand]);

  const startMonitoring = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/social/run`, { region, brand });
      toast.success("Social media monitoring started!");
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/social/latest?region=${region}&brand=${brand}`);
          if (res.data.run?.status === "completed" || res.data.run?.status === "failed") {
            clearInterval(pollInterval);
            setRunning(false);
            fetchData();
            if (res.data.run?.status === "completed") {
              toast.success("Social monitoring complete!");
            } else {
              toast.error("Monitoring failed. Check settings.");
            }
          }
        } catch (e) {
          console.debug("Social monitoring poll failed:", e?.message || e);
        }
      }, 5000);
      
      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        setRunning(false);
        fetchData();
      }, 300000);
      
    } catch (error) {
      setRunning(false);
      toast.error("Failed to start monitoring");
    }
  };

  const toggleInsight = (key) => {
    const next = new Set(expandedInsights);
    next.has(key) ? next.delete(key) : next.add(key);
    setExpandedInsights(next);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto space-y-6" data-testid="social-loading">
        <div className="flex items-center justify-between animate-fadeIn">
          <div>
            <Skeleton className={`h-8 w-56 mb-2 ${darkMode ? 'bg-[#21262d]' : ''}`} />
            <Skeleton className={`h-4 w-72 ${darkMode ? 'bg-[#21262d]' : ''}`} />
          </div>
          <Skeleton className={`h-10 w-40 rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className={`h-28 rounded-xl ${darkMode ? 'bg-[#21262d]' : ''}`} />
          ))}
        </div>
        <Skeleton className={`h-64 rounded-xl ${darkMode ? 'bg-[#21262d]' : ''}`} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="social-media-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Social Media Monitoring
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Track competitor activity across social platforms
          </p>
        </div>
        <Button 
          onClick={startMonitoring} 
          disabled={running}
          className="bg-[#DC143C] hover:bg-[#b91031] text-white"
          data-testid="run-social-scan-btn"
        >
          {running ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Scanning...
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              Run Social Scan
            </>
          )}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`rounded-xl p-5 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Posts</span>
            <Share2 className="w-5 h-5 text-[#DC143C]" />
          </div>
          <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {stats?.total_posts || 0}
          </div>
        </div>

        <div className={`rounded-xl p-5 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Competitors Tracked</span>
            <Users className="w-5 h-5 text-blue-500" />
          </div>
          <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {stats?.competitors_with_social || 0}
            <span className={`text-sm font-normal ml-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              / {stats?.total_competitors || 0}
            </span>
          </div>
        </div>

        <div className={`rounded-xl p-5 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Scans Completed</span>
            <BarChart3 className="w-5 h-5 text-emerald-500" />
          </div>
          <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {stats?.completed_runs || 0}
          </div>
        </div>

        <div className={`rounded-xl p-5 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Platforms</span>
            <RefreshCw className="w-5 h-5 text-purple-500" />
          </div>
          <div className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {stats?.posts_by_platform ? Object.keys(stats.posts_by_platform).length : 0}
          </div>
        </div>
      </div>

      {/* Platform Breakdown */}
      {stats?.posts_by_platform && Object.keys(stats.posts_by_platform).length > 0 && (
        <div className={`rounded-xl p-5 ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <h3 className={`font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Posts by Platform
          </h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(stats.posts_by_platform).map(([platform, count]) => {
              const config = platformIcons[platform] || platformIcons.twitter;
              const Icon = config.icon;
              return (
                <div 
                  key={platform}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}
                >
                  <div className={`p-1.5 rounded-lg ${config.bg}`}>
                    <Icon className={`w-4 h-4 ${config.color}`} />
                  </div>
                  <span className={`capitalize font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {platform}
                  </span>
                  <Badge variant="secondary" className={darkMode ? 'bg-[#30363d] text-gray-300' : ''}>
                    {count}
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Latest Run Insights */}
      {insights && (
        <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
            <div className="flex items-center justify-between">
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                AI-Generated Insights
              </h3>
              {latestRun && (
                <span className={`text-xs flex items-center gap-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <Clock className="w-3 h-3" />
                  {new Date(latestRun.completed_at).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
          <div className="p-5 space-y-4">
            {/* Platform Insights */}
            {insights.platform_insights && Object.keys(insights.platform_insights).length > 0 && (
              <Collapsible open={expandedInsights.has('platform_insights')}>
                <CollapsibleTrigger 
                  onClick={() => toggleInsight('platform_insights')}
                  className={`w-full flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-[#21262d] hover:bg-[#30363d]' : 'bg-gray-50 hover:bg-gray-100'}`}
                >
                  <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Platform-Specific Insights
                  </span>
                  {expandedInsights.has('platform_insights') ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-3">
                  {Object.entries(insights.platform_insights).map(([platform, data]) => {
                    const config = platformIcons[platform] || platformIcons.twitter;
                    const Icon = config.icon;
                    return (
                      <div key={platform} className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`p-1.5 rounded-lg ${config.bg}`}>
                            <Icon className={`w-4 h-4 ${config.color}`} />
                          </div>
                          <span className={`font-medium capitalize ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {platform}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {data.content_themes && (
                            <div>
                              <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Content Themes
                              </span>
                              <ul className={`mt-1 space-y-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {(Array.isArray(data.content_themes) ? data.content_themes : [data.content_themes]).map((theme) => (
                                  <li key={theme}>• {theme}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {data.engagement_tactics && (
                            <div>
                              <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Engagement Tactics
                              </span>
                              <p className={`mt-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {Array.isArray(data.engagement_tactics) ? data.engagement_tactics.join(', ') : data.engagement_tactics}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Recommended Actions */}
            {insights.recommended_actions && insights.recommended_actions.length > 0 && (
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Recommended Actions
                </h4>
                <ul className="space-y-2">
                  {insights.recommended_actions.map((action) => (
                    <li key={action} className={`flex items-start gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <span className="text-[#DC143C] mt-0.5">•</span>
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Opportunities */}
            {insights.opportunities && insights.opportunities.length > 0 && (
              <div className={`p-4 rounded-lg border-l-4 border-[#DC143C] ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}>
                <h4 className={`font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Opportunities for BlackSwan
                </h4>
                <ul className="space-y-2">
                  {insights.opportunities.map((opp) => (
                    <li key={opp} className={`flex items-start gap-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      <TrendingUp className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      {opp}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Posts */}
      {posts.length > 0 && (
        <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <div className={`px-5 py-4 border-b ${darkMode ? 'border-[#21262d]' : 'border-gray-100'}`}>
            <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Recent Social Profiles Scanned
            </h3>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-[#21262d]">
            {posts.slice(0, 10).map((post, i) => {
              const config = platformIcons[post.platform] || platformIcons.twitter;
              const Icon = config.icon;
              return (
                <div key={post.id || i} className={`px-5 py-4 ${darkMode ? 'hover:bg-[#21262d]' : 'hover:bg-gray-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${config.bg}`}>
                        <Icon className={`w-5 h-5 ${config.color}`} />
                      </div>
                      <div>
                        <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {post.competitor_name}
                        </div>
                        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {post.post_summary?.slice(0, 80) || post.platform}
                          {post.post_summary?.length > 80 && '...'}
                        </div>
                      </div>
                    </div>
                    <a 
                      href={post.profile_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={`text-sm flex items-center gap-1 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!insights && posts.length === 0 && !loading && (
        <div className={`rounded-xl p-16 text-center ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
          <Share2 className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
          <h3 className={`font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            No social data yet
          </h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Make sure your competitors have social media URLs configured, then run a scan
          </p>
          <Button 
            onClick={startMonitoring}
            disabled={running}
            className="bg-[#DC143C] hover:bg-[#b91031] text-white"
          >
            {running ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Run First Scan
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
